<app-dialog-header [title]="'Data Import - ' + getEntityTypeLabel()" (closeClicked)="close()">
</app-dialog-header>

<mat-dialog-content class="import-wizard-content">
  <mat-stepper #stepper>
    <!-- STEP 1: Select Data Type & Download Template -->
    <mat-step label="Select & Download">
      <div class="step-content">
        <!-- Only show entity type selection if not pre-set via dialog data -->
        @if (!hasPresetEntityType) {
        <h3>What data would you like to import?</h3>

        <mat-radio-group [(ngModel)]="entityType" class="entity-type-group">
          <mat-radio-button value="RESOURCE" class="entity-option">
            <mat-icon>person</mat-icon>
            <span>Resources</span>
          </mat-radio-button>
          <mat-radio-button value="SOR_CODE" class="entity-option">
            <mat-icon>list_alt</mat-icon>
            <span>SOR Codes</span>
          </mat-radio-button>
        </mat-radio-group>
        } @else {
        <h3>Import {{ getEntityTypeLabel() }}</h3>
        }

        <div class="download-section">
          <p class="instruction-text">
            Download a template, edit it in Excel, then return here to upload your changes.
          </p>

          <div class="download-buttons">
            <button mat-raised-button color="primary" (click)="downloadTemplate(true)" [disabled]="isDownloading">
              @if (isDownloading) {
              <mat-spinner diameter="20"></mat-spinner>
              } @else {
              <mat-icon>file_download</mat-icon>
              }
              Download Template with Existing Data
            </button>

            <button mat-stroked-button (click)="downloadTemplate(false)" [disabled]="isDownloading">
              <mat-icon>note_add</mat-icon>
              Download Empty Template
            </button>
          </div>

          <div class="info-box">
            <mat-icon>info</mat-icon>
            <span>
              <strong>Adding new records only?</strong> Use the empty template.<br>
              <strong>Updating existing records?</strong> Download with existing data to preserve IDs.
            </span>
          </div>

          <div class="info-box">
            <mat-icon>help_outline</mat-icon>
            <span>Templates include a "Reference Data" sheet with valid values for lookup fields, and an
              "Instructions" sheet explaining each column.</span>
          </div>
        </div>

        <div class="step-actions">
          <span class="spacer"></span>
          <button mat-flat-button color="primary" matStepperNext>
            Next
            <mat-icon>arrow_forward</mat-icon>
          </button>
        </div>
      </div>
    </mat-step>

    <!-- STEP 2: Upload File -->
    <mat-step label="Upload">
      <div class="step-content">
        <h3>Upload your edited Excel file</h3>

        <div class="upload-zone" (drop)="onFileDrop($event)" (dragover)="onDragOver($event)"
          [class.has-file]="selectedFile">
          @if (!selectedFile) {
          <mat-icon class="upload-icon">cloud_upload</mat-icon>
          <p>Drag and drop your Excel file here</p>
          <p class="or-text">or</p>
          <input type="file" #fileInput accept=".xlsx,.xls" (change)="onFileSelected($event)" hidden>
          <button mat-raised-button (click)="fileInput.click()">
            <mat-icon>folder_open</mat-icon>
            Browse Files
          </button>
          } @else {
          <mat-icon class="file-icon">description</mat-icon>
          <p class="file-name">{{ selectedFile.name }}</p>
          <p class="file-size">{{ (selectedFile.size / 1024).toFixed(1) }} KB</p>
          <button mat-icon-button (click)="clearFile()" class="clear-file">
            <mat-icon>close</mat-icon>
          </button>
          }
        </div>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon>
            Back
          </button>
          <span class="spacer"></span>
          <button mat-flat-button color="primary" [disabled]="!selectedFile || isParsing || isValidating"
            (click)="parseAndValidate(stepper)">
            @if (isParsing || isValidating) {
            <mat-spinner diameter="20"></mat-spinner>
            } @else {
            <mat-icon>check_circle</mat-icon>
            }
            <span>{{ isParsing ? 'Parsing...' : isValidating ? 'Validating...' : 'Validate' }}</span>
          </button>
        </div>
      </div>
    </mat-step>

    <!-- STEP 3: Review & Commit -->
    <mat-step label="Review">
      <div class="step-content">
        <h3>Validation Results</h3>

        @if (validationResponse) {
        <!-- Summary Cards -->
        <div class="summary-cards">
          <mat-card class="summary-card">
            <mat-card-content>
              <div class="summary-value">{{ validationResponse.summary.total }}</div>
              <div class="summary-label">Total Rows</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="summary-card success">
            <mat-card-content>
              <div class="summary-value">{{ validationResponse.summary.newRecords }}</div>
              <div class="summary-label">New</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="summary-card info">
            <mat-card-content>
              <div class="summary-value">{{ validationResponse.summary.updatedRecords }}</div>
              <div class="summary-label">Updated</div>
            </mat-card-content>
          </mat-card>

          @if (validationResponse.summary.unchangedRecords > 0) {
          <mat-card class="summary-card">
            <mat-card-content>
              <div class="summary-value">{{ validationResponse.summary.unchangedRecords }}</div>
              <div class="summary-label">Unchanged</div>
            </mat-card-content>
          </mat-card>
          }

          @if (validationResponse.summary.deletedRecords > 0) {
          <mat-card class="summary-card warn">
            <mat-card-content>
              <div class="summary-value">{{ validationResponse.summary.deletedRecords }}</div>
              <div class="summary-label">Deleted</div>
            </mat-card-content>
          </mat-card>
          }

          <mat-card class="summary-card" [class.error]="validationResponse.summary.errorCount > 0">
            <mat-card-content>
              <div class="summary-value">{{ validationResponse.summary.errorCount }}</div>
              <div class="summary-label">Errors</div>
            </mat-card-content>
          </mat-card>
        </div>

        @if (validationResponse.valid) {
        <!-- Success State -->
        <div class="validation-success">
          <mat-icon>check_circle</mat-icon>
          <p>All records passed validation. Ready to import.</p>
        </div>

        @if (isWithinCoreHours()) {
        <div class="core-hours-warning">
          <mat-icon>warning</mat-icon>
          <span>
            <strong>Warning:</strong> You are importing during core business hours (8am - 6pm).
            Data uploads are recommended outside these times to minimize system impact.
          </span>
        </div>
        }
        } @else {
        <!-- Error State -->
        <div class="validation-error">
          <mat-icon>error</mat-icon>
          <p>Import cannot proceed until all errors are fixed.</p>
        </div>

        <div class="error-table-container">
          <table mat-table [dataSource]="getErrorsForDisplay()" class="error-table">
            <ng-container matColumnDef="rowIndex">
              <th mat-header-cell *matHeaderCellDef>Row</th>
              <td mat-cell *matCellDef="let error">{{ error.rowIndex + 2 }}</td>
              <!-- +2 for header row and 0-based index -->
            </ng-container>

            <ng-container matColumnDef="field">
              <th mat-header-cell *matHeaderCellDef>Field</th>
              <td mat-cell *matCellDef="let error">{{ error.field }}</td>
            </ng-container>

            <ng-container matColumnDef="message">
              <th mat-header-cell *matHeaderCellDef>Error</th>
              <td mat-cell *matCellDef="let error">{{ error.message }}</td>
            </ng-container>

            <ng-container matColumnDef="value">
              <th mat-header-cell *matHeaderCellDef>Value</th>
              <td mat-cell *matCellDef="let error">{{ error.value || '(empty)' }}</td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="errorDisplayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: errorDisplayedColumns;"></tr>
          </table>
        </div>
        }
        }

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon>
            Back
          </button>
          <span class="spacer"></span>
          @if (validationResponse && !validationResponse.valid) {
          <button mat-flat-button color="warn" (click)="clearFile(); stepper.previous()">
            <mat-icon>refresh</mat-icon>
            Fix Errors & Re-upload
          </button>
          } @else {
          <button mat-flat-button color="primary" [disabled]="!validationResponse?.valid || isCommitting"
            (click)="commitImport(stepper)">
            @if (isCommitting) {
            <mat-spinner diameter="20"></mat-spinner>
            } @else {
            <mat-icon>save</mat-icon>
            }
            <span>{{ isCommitting ? 'Importing...' : 'Import ' + (validationResponse?.summary?.total || 0) + ' Records' }}</span>
          </button>
          }
        </div>
      </div>
    </mat-step>

    <!-- STEP 4: Complete -->
    <mat-step label="Complete">
      <div class="step-content completion-step">
        @if (commitSuccess) {
        <mat-icon class="success-icon">check_circle</mat-icon>
        <h3>Import Completed Successfully!</h3>
        <p>{{ commitMessage }}</p>
        } @else {
        <mat-icon class="error-icon">error</mat-icon>
        <h3>Import Failed</h3>
        <p>{{ commitMessage }}</p>
        }

        <div class="step-actions centered">
          <button mat-flat-button color="primary" (click)="close()">
            <mat-icon>done</mat-icon>
            Done
          </button>
        </div>
      </div>
    </mat-step>
  </mat-stepper>
</mat-dialog-content>
